import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { patientApi } from '../services/patientApi';
import { buildPatientViewModel, getKnownDepartments } from '../utils/patientViewModel';

const knownDepartments = getKnownDepartments();

const DashboardPage = () => {
  const [pacientes, setPacientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState('hospitalizado');
  const [deptFilter, setDeptFilter] = useState('todos');
  const navigate = useNavigate();

  useEffect(() => {
    patientApi.getPatients()
      .then((response) => setPacientes((response.data || []).map(buildPatientViewModel)))
      .catch(() => setError('No se pudieron cargar los pacientes.'))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => { document.title = 'Panel de pacientes | PlantaCare'; }, []);

  const visibles = useMemo(() => {
    const query = busqueda.trim().toLowerCase();
    return pacientes.filter((paciente) => {
      const coincide = !query || paciente.nombre?.toLowerCase().includes(query) || paciente.historia_clinica?.toLowerCase().includes(query);
      const estado = filtro === 'todos' || (filtro === 'uci' ? paciente.en_uci : paciente.estado === filtro);
      const deptMatch = deptFilter === 'todos' || (deptFilter === 'Otros' ? !((paciente.department || '') && knownDepartments.includes(paciente.department)) : (paciente.department || '') === deptFilter);
      return coincide && estado && deptMatch;
    });
  }, [pacientes, busqueda, filtro, deptFilter]);

  const pacientesFiltradosPorEstado = useMemo(() => pacientes.filter((paciente) => filtro === 'todos' || (filtro === 'uci' ? paciente.en_uci : paciente.estado === filtro)), [pacientes, filtro]);
  const enUci = pacientes.filter((paciente) => paciente.en_uci).length;
  const departments = [...knownDepartments];
  const deptSummary = departments.map((departmentName) => {
    const list = pacientesFiltradosPorEstado.filter((paciente) => (paciente.department || '').toLowerCase() === departmentName.toLowerCase());
    return { department: departmentName, total: list.length, inIcu: list.filter((paciente) => paciente.en_uci).length };
  });
  const others = pacientesFiltradosPorEstado.filter((paciente) => !departments.map((departmentName) => departmentName.toLowerCase()).includes((paciente.department || '').toLowerCase()));
  const othersSummary = { department: 'Otros', total: others.length, inIcu: others.filter((paciente) => paciente.en_uci).length };

  const getDeptCardClasses = (active) => active
    ? 'border-teal-500 bg-teal-50 shadow-sm ring-2 ring-teal-200'
    : 'border-slate-200 bg-white hover:shadow-md';

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Pacientes en planta</h1>
            <p className="mt-1 text-sm text-slate-500">{pacientes.length} registrados · {enUci} actualmente en UCI</p>
          </div>
          <button onClick={() => navigate('/paciente/new')} className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-teal-700">
            <Plus className="h-4 w-4" /> Nuevo paciente
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          {deptSummary.map((summary) => {
            const pct = summary.total ? Math.round((summary.inIcu / summary.total) * 100) : 0;
            const active = deptFilter === summary.department;
            return (
              <button key={summary.department} type="button" onClick={() => setDeptFilter(active ? 'todos' : summary.department)} className={`group flex flex-col items-start justify-between rounded-2xl border p-4 text-left shadow-sm focus:outline-none transition ${getDeptCardClasses(active)}`}>
                <div className="w-full flex items-center justify-between">
                  <p className={`text-sm ${active ? 'text-teal-700' : 'text-slate-500'}`}>{summary.department}</p>
                  <p className={`text-xs ${active ? 'text-teal-600' : 'text-slate-400'} group-hover:text-teal-600`}>Ver</p>
                </div>
                <div className="mt-3 w-full">
                  <p className={`text-2xl font-semibold ${active ? 'text-teal-900' : 'text-slate-900'}`}>{summary.total}</p>
                  <p className={`mt-1 text-sm ${active ? 'text-teal-700' : 'text-slate-500'}`}>En UCI: <span className="font-medium text-red-600">{summary.inIcu}</span></p>
                  <div className="mt-3 h-2 w-full rounded-full bg-slate-100"><div className="h-2 rounded-full bg-red-500 transition-all" style={{ width: `${pct}%` }} /></div>
                  <p className={`mt-1 text-xs ${active ? 'text-teal-600' : 'text-slate-400'}`}>{pct}% de pacientes en UCI</p>
                </div>
              </button>
            );
          })}
          <button type="button" onClick={() => setDeptFilter(deptFilter === 'Otros' ? 'todos' : 'Otros')} className={`flex flex-col items-start justify-between rounded-2xl border p-4 text-left shadow-sm transition ${getDeptCardClasses(deptFilter === 'Otros')}`}>
            <p className="text-sm text-slate-500">Otros</p>
            <div className="mt-3 w-full">
              <p className="text-2xl font-semibold text-slate-900">{othersSummary.total}</p>
              <p className="mt-1 text-sm text-slate-500">En UCI: <span className="font-medium text-red-600">{othersSummary.inIcu}</span></p>
              <div className="mt-3 h-2 w-full rounded-full bg-slate-100"><div className="h-2 rounded-full bg-red-500" style={{ width: `${othersSummary.total ? Math.round((othersSummary.inIcu / othersSummary.total) * 100) : 0}%` }} /></div>
              <p className="mt-1 text-xs text-slate-400">{othersSummary.total ? Math.round((othersSummary.inIcu / othersSummary.total) * 100) : 0}% de pacientes en UCI</p>
            </div>
          </button>
        </div>

        <div className="flex flex-wrap gap-3">
          <label className="relative flex-1 min-w-[240px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={busqueda} onChange={(event) => setBusqueda(event.target.value)} placeholder="Buscar por nombre o expediente" className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm shadow-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100" />
          </label>
          <select value={deptFilter} onChange={(event) => setDeptFilter(event.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm shadow-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100">
            <option value="todos">Todos los departamentos</option>
            {knownDepartments.map((departmentName) => <option key={departmentName} value={departmentName}>{departmentName}</option>)}
            <option value="Otros">Otros</option>
          </select>
          <select value={filtro} onChange={(event) => setFiltro(event.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm shadow-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100">
            <option value="todos">Todos</option>
            <option value="hospitalizado">Hospitalizados</option>
            <option value="uci">En UCI</option>
            <option value="alta">De alta</option>
          </select>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          {cargando && (<div className="space-y-3">{[0, 1, 2].map((index) => <div key={index} className="h-20 animate-pulse rounded-xl bg-slate-100" />)}</div>)}
          {!cargando && error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
          {!cargando && !error && visibles.length === 0 && (<div className="rounded-xl border border-dashed border-slate-200 p-10 text-center text-sm text-slate-500">No hay pacientes que coincidan con la búsqueda.</div>)}
          {!cargando && !error && visibles.length > 0 && (
            <div className="space-y-3">
              {visibles.map((paciente) => (
              
                <button key={paciente.id} onClick={() => navigate(`/pacientes/${paciente.id}`)} className="flex w-full flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-teal-300 hover:shadow-sm">
                  <div>
                    <p className="font-semibold text-slate-900">{paciente.nombre}</p>
                    <p className="mt-1 text-sm text-slate-500">Exp. {paciente.historia_clinica}{paciente.edad ? ` · ${paciente.edad} años` : ''}{paciente.habitacion ? ` · Hab. ${paciente.habitacion}` : ''}</p>
                    <p className="mt-2 text-sm text-slate-600">{paciente.diagnostico || 'Sin diagnóstico registrado'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {paciente.en_uci ? (<span className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700">Actualmente en UCI</span>) : (<span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium capitalize text-slate-700">{paciente.estado || 'hospitalizado'}</span>)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default DashboardPage;
